import axios from 'axios';

import { clearAccessToken, setAccessToken } from './token-store';

/**
 * Renews the access token using the httpOnly refresh cookie.
 *
 * Single-flight by design, and that is the whole difficulty. Refresh tokens
 * rotate: each call retires the token it consumed. If five expired requests each
 * fired their own refresh, the first would succeed and the other four would
 * present an already-retired token — which a session store treats as theft,
 * logging the user out. A naive implementation therefore turns every token
 * expiry into a logout storm.
 *
 * So concurrent callers all await one in-flight promise and replay with the same
 * new token.
 */

let inFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const { data } = await axios({
    method: 'post',
    url: `${import.meta.env.VITE_API_URL}/api/auth/refresh`,
    data: {},
    // The refresh token rides in the httpOnly cookie; there is nothing to put in
    // the body, and without credentials the cookie is not sent at all.
    withCredentials: true,
  });

  const token = data?.jwt;

  if (!token) {
    throw new Error('Refresh response contained no token');
  }

  setAccessToken(token);

  return token;
}

export function refreshAccessToken(): Promise<string> {
  if (!inFlight) {
    inFlight = performRefresh()
      .catch((error) => {
        // Clear on failure so a dead session cannot masquerade as a live one via
        // a stale token still sitting in memory.
        clearAccessToken();
        throw error;
      })
      .finally(() => {
        // Released whether it resolved or rejected. Leaving a settled promise
        // cached would mean every later caller replays one old outcome forever —
        // a permanent logout after a single transient network blip.
        inFlight = null;
      });
  }

  return inFlight;
}

/**
 * Exposed for tests only: the module-level latch outlives individual tests.
 */
export function resetRefreshState(): void {
  inFlight = null;
}
